import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-sse";
import { print } from "graphql";

import { clearStoredTokens, getValidAccessToken, refreshAccessToken } from "../utils/authRefresh";

type GraphQLErrorWithExtensionsCode = {
  extensions?: { code?: string };
};

const GRAPHQL_HTTP_ENDPOINT = typeof window === "undefined" ? "/api/graphql" : `${window.location.origin}/api/graphql`;

const GRAPHQL_SSE_ENDPOINT =
  typeof window === "undefined" ? "/api/graphql/stream" : `${window.location.origin}/api/graphql/stream`;

const httpLink = new HttpLink({ uri: GRAPHQL_HTTP_ENDPOINT });

const authLink = setContext(async (_, { headers }) => {
  const accessToken = await getValidAccessToken();
  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
  };
});

let isRefreshing = false;
let pendingRequests: Array<(newAccessToken: string | null) => void> = [];

const addPendingRequest = (cb: (newAccessToken: string | null) => void) => {
  pendingRequests.push(cb);
};

const resolvePendingRequests = async (accessToken?: string | null) => {
  const newAccess = accessToken ?? (await getValidAccessToken({ minTtlSeconds: 0 }));
  pendingRequests.forEach((cb) => cb(newAccess ?? null));
  pendingRequests = [];
};

const rejectPendingRequests = (err?: unknown) => {
  pendingRequests.forEach((cb) => cb(null));
  pendingRequests = [];
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  interface MaybeServerError {
    statusCode?: number;
    result?: { errors?: Array<{ message?: string }> };
  }
  const ne = networkError as MaybeServerError | null | undefined;
  const statusCode = ne?.statusCode;
  const isUnauthorizedMsg = Boolean(graphQLErrors?.some((e) => e.message === "Unauthorized"));
  const isUnauthenticatedCode = Boolean(
    graphQLErrors?.some((e) => (e as GraphQLErrorWithExtensionsCode).extensions?.code === "UNAUTHENTICATED")
  );
  const isUserNotFoundMsg = Boolean(
    graphQLErrors?.some((e) => e.message === "Context creation failed: User not found")
  );

  const unauthorized = Boolean(
    statusCode === 401 || isUnauthorizedMsg || isUnauthenticatedCode || (statusCode === 400 && isUserNotFoundMsg)
  );

  if (!unauthorized) {
    return;
  }

  if (operation.operationName === "RefreshToken") {
    clearStoredTokens("refresh_operation_failed");
    return;
  }

  if (isRefreshing) {
    // queue the subsequent failed operations
    return new Observable((observer) => {
      addPendingRequest(async (token: string | null) => {
        if (!token) {
          observer.error(networkError ?? new Error("Unauthorized"));
          return;
        }
        operation.setContext(({ headers = {} }) => ({
          headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
        }));
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      });
    });
  }

  isRefreshing = true;
  return new Observable((observer) => {
    // queue the first failed operation
    addPendingRequest(async (token: string | null) => {
      if (!token) {
        observer.error(networkError ?? new Error("Unauthorized"));
        return;
      }
      operation.setContext(({ headers = {} }) => ({
        headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
      }));
      forward(operation).subscribe({
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer),
      });
    });

    refreshAccessToken()
      .then(async (result) => {
        isRefreshing = false;
        if (!result?.accessToken) {
          rejectPendingRequests(networkError ?? new Error("Unauthorized"));
          return;
        }
        await resolvePendingRequests(result.accessToken);
      })
      .catch((err) => {
        isRefreshing = false;
        rejectPendingRequests(err);
      });
  });
});

// afterware removed; tokens are managed via refresh flow

const createSseApolloLink = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return new ApolloLink((operation) => {
    return new Observable((observer) => {
      const sseClient = createClient({
        url: GRAPHQL_SSE_ENDPOINT,
        headers: async () => {
          const accessToken = await getValidAccessToken({ minTtlSeconds: 30 });
          if (!accessToken) {
            return {};
          }
          return { Authorization: `Bearer ${accessToken}` };
        },
      });

      const dispose = sseClient.subscribe(
        {
          operationName: operation.operationName,
          query: print(operation.query),
          variables: operation.variables,
        },
        {
          next: (value) => observer.next(value),
          error: (err) => observer.error(err),
          complete: () => observer.complete(),
        }
      );

      return () => {
        dispose();
        sseClient.dispose();
      };
    });
  });
};

const httpAuthedLink = ApolloLink.from([authLink, errorLink, httpLink]);
const sseLink = createSseApolloLink();

export const apolloClient = new ApolloClient({
  link: sseLink
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return definition.kind === "OperationDefinition" && definition.operation === "subscription";
        },
        sseLink,
        httpAuthedLink
      )
    : httpAuthedLink,
  cache: new InMemoryCache(),
});
